<script setup lang="ts">
import type { CompletionSpotlight, CompletionSpotlightLinkClick } from '~kui/components/KUI/Unified/Completion/KUIUniCompletion.vue';

import KUIUniCompletion from '~kui/components/KUI/Unified/Completion/KUIUniCompletion.vue';

import type { LearningAssetCompletionResult, LearningAssetEventProps } from '~/composables/unified/useLearningAssetFlow';
import type { AnalyticsOrigin } from '~/constants/analyticsOrigin';
import type { SpotlightViewModel } from '~/types/unified';

import UnifiedAccountVerificationDialog from '~/components/Unified/Profile/UnifiedAccountVerificationDialog.vue';
import { useActivityOrchestrator } from '~/composables/unified/useActivityOrchestrator';
import { useFeedbackSurvey } from '~/composables/unified/useFeedbackSurvey';
import { useGhostCompletionCtas } from '~/composables/unified/useGhostCompletionCtas';
import { usePrizeDrawFlow } from '~/composables/unified/usePrizeDrawFlow';
import { useRewardsSuppressed } from '~/composables/unified/useRewardsSuppressed';
import { useSpotlight } from '~/composables/unified/useSpotlight';
import { AnalyticsOrigin as Origin } from '~/constants/analyticsOrigin';
import { UNIFIED_ROUTE_ACTIVE_CAMPAIGN_TERMS_CONDITIONS } from '~/constants/routes/unified';
import { CoachActivityInfoType, SpotlightTargetType } from '~/graphql/generated/graphql';
import { assetSpotlightTarget, collectionSpotlightTarget } from '~/utils/unified/mapSpotlight';

/**
 * The celebration an activity ends on: what it paid, the collection it completed, the prize draw
 * it entered, its own and the collection's spotlights, and the collection's questionnaire when the
 * server says this screen is the surface that asks it.
 *
 * All of it hangs off the orchestrator's completion context rather than off the activity, which is
 * why it lives apart from {@see LearningAssetActivity} — that component is the sitting itself.
 */
interface Props {
  /** Whether this screen is the one on show, which is when a questionnaire counts as seen. */
  active: boolean;
  asset: {
    id: string;
    name: string;
    slug?: string;
    coins?: number | null;
    partnerId?: string | null;
    partnerName?: string | null;
    spotlight?: SpotlightViewModel | null;
  } | null;
  /** The asset's analytics identity, as the flow stamps it on every asset event. */
  assetEventProps: LearningAssetEventProps;
  /** What finishing the activity returned: the coins, the re-do flag and the profile deltas. */
  completionResult: LearningAssetCompletionResult | null;
  collectionId?: string;
  collectionName?: string;
  collectionSlug?: string;
  origin?: AnalyticsOrigin;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  /** The student is finished with the celebration; the activity flow decides what follows. */
  done: [];
  considerationsViewed: [];
  clarityViewed: [];
}>();

const orchestrator = useActivityOrchestrator();

if (!orchestrator) {
  throw new Error('LearningAssetCompletionScreen must be used within an activity orchestrator');
}

const { t } = useI18n();
const router = useRouter();
const accessibilityStore = useAccessibilityStore();
const { isConsumerGhost } = useAuth();
const rewardsSuppressed = useRewardsSuppressed();

const previewMode = orchestrator.previewMode ?? false;
const collectionCompletion = orchestrator.collectionCompletion;
const collectionId = computed(() => props.collectionId ?? '');

// The confirm-prize dialog renders this as an `external` new-tab NuxtLink, which
// bypasses the router base — so the cluster prefix is resolved here or an AU user
// lands on the root cluster, where the campaign doesn't exist.
const campaignTermsUrl = computed(() =>
  collectionCompletion?.value?.prize?.campaignId
    ? router.resolve(UNIFIED_ROUTE_ACTIVE_CAMPAIGN_TERMS_CONDITIONS(collectionCompletion.value.prize.campaignId)).href
    : '',
);

// The activity's own spotlight shows on its completion, and the collection's rides along once the
// collection is finished, with or without a badge to hang it on, so finishing a collection's last
// activity can show both. The collection's identity comes from the args this activity was opened
// with, since the completion context has no copy of it.
const assetSpotlight = useSpotlight({
  source: computed(() => props.asset
    ? { target: assetSpotlightTarget(props.asset), spotlight: props.asset.spotlight ?? null }
    : null),
  origin: Origin.Completion,
});

const collectionSpotlight = useSpotlight({
  source: computed(() => collectionId.value
    ? {
        target: collectionSpotlightTarget({
          id: collectionId.value,
          slug: props.collectionSlug,
          name: props.collectionName,
          partnerId: collectionCompletion?.value?.partnerId,
          partnerName: collectionCompletion?.value?.partnerName,
        }),
        spotlight: collectionCompletion?.value?.spotlight ?? null,
      }
    : null),
  origin: Origin.Completion,
});

const spotlights = computed<CompletionSpotlight[]>(() => [
  ...(assetSpotlight.spotlight.value ? [{ key: SpotlightTargetType.LearningAsset, ...assetSpotlight.spotlight.value }] : []),
  ...(collectionSpotlight.spotlight.value ? [{ key: SpotlightTargetType.Collection, ...collectionSpotlight.spotlight.value }] : []),
]);

const trackSpotlightLinkClick = ({ key, link }: CompletionSpotlightLinkClick) =>
  (key === SpotlightTargetType.LearningAsset ? assetSpotlight : collectionSpotlight).trackLinkClick(link);

const surveySharedWithTeacher = computed(() => collectionCompletion?.value?.feedbackSurveySharedWithTeacher ?? false);
// The server names the one surface that asks each questionnaire: this screen, or a coach card
// after the collection is completed. Asking on both would double-ask, and first-write-wins
// would waste whichever answer came second.
const askedAtCompletion = computed(() => collectionCompletion?.value?.feedbackSurveyAskedAtCompletion ?? false);
const completionSurvey = computed(() =>
  (askedAtCompletion.value ? collectionCompletion?.value?.feedbackSurvey ?? null : null));
const questionCount = computed(() => completionSurvey.value?.length ?? 0);
const {
  submitting: feedbackSurveySubmitting,
  submit: submitFeedbackSurvey,
  trackStarted: trackSurveyStarted,
  trackSkipped: trackSurveySkipped,
} = useFeedbackSurvey(
  computed(() => ({ collectionId: collectionId.value })),
  questionCount,
  surveySharedWithTeacher,
);

watch(
  () => props.active && questionCount.value > 0,
  (hasFeedback) => {
    if (hasFeedback && !previewMode) {
      trackSurveyStarted();
    }
  },
);

const feedbackSurveyError = ref<string | null>(null);
const handleFeedbackSurveySubmitted = async (responses: Record<string, unknown>) => {
  if (previewMode) {
    return;
  }
  feedbackSurveyError.value = null;
  const { submitted, coinsAwarded } = await submitFeedbackSurvey(responses);
  if (!submitted) {
    feedbackSurveyError.value = t('unified.errors.genericFailed');

    return;
  }
  // The answer rides on the visit like everything else done in here, so the return is
  // acknowledged for it and the coach can thank them for it in its own words.
  orchestrator.writeActivityInfoEvent?.({
    activityInfoType: CoachActivityInfoType.FeedbackSurveyAnswered,
    data: { collectionId: collectionId.value, coinsAwarded },
  });
};

const {
  prizeOutcome,
  confirmPrizeLoading,
  confirmPrizeError,
  showProfileVerification,
  completionPrize,
  skillAnswer,
  handleSkipPrizeDraw,
  handleConfirmEntry,
  handleCancelPrizeDraw,
  handleProfileVerified,
} = usePrizeDrawFlow(collectionCompletion, { origin: props.origin });

/**
 * The completion celebration's reward figures, zeroed once here for schools sessions
 * (CP-11267) so the template binds plain values instead of repeating the rule per prop.
 * A re-do zeroes the coins too: the row's historical awardedCoins would otherwise render
 * as "You've earned" on a completion that deposits nothing.
 */
const completionRewards = computed(() => {
  if (rewardsSuppressed.value) {
    return {
      coinsEarned: 0,
      totalCoins: undefined,
      animateCoins: false,
      bonusCoins: null,
      prize: null,
      prizeOutcome: null,
    };
  }
  const isReDo = props.completionResult?.wasReCompletion ?? false;

  return {
    coinsEarned: isReDo ? 0 : props.completionResult?.awardedCoins ?? props.asset?.coins ?? 0,
    totalCoins: collectionCompletion?.value?.totalCoins ?? props.completionResult?.totalCoins,
    animateCoins: !isReDo && !!props.completionResult?.awardedCoins,
    bonusCoins: collectionCompletion?.value?.isFullyCompleted ? collectionCompletion.value?.bonusCoins ?? null : null,
    prize: completionPrize.value,
    prizeOutcome: prizeOutcome.value,
  };
});

/**
 * The partial-completion pitch, resolved here so KUI stays policy-free (CP-11267): the shared
 * component renders whatever string it is handed, and only the schools door swaps in the
 * badge-only wording.
 */
const partialUnlockMessage = computed(() => (rewardsSuppressed.value
  ? t('unified.activity.completion.partialUnlockBadgeOnlyMessage', { total: collectionCompletion?.value?.activities?.length ?? 0 })
  : null));

const {
  primaryLabel: primaryCtaLabel,
  secondaryLabel: secondaryCtaLabel,
  primaryLoading: completionCtaLoading,
  secondaryLoading: secondaryCtaLoading,
  handleContinue: handleCompletionContinue,
  handleSecondary: handleSecondaryCta,
  handleCtaShown,
} = useGhostCompletionCtas(() => props, { previewMode, orchestrator, continueFlow: () => emit('done') });

const handleFeedbackSurveySkipped = (answeredCount: number) => {
  if (previewMode) {
    return;
  }
  trackSurveySkipped(answeredCount, 'skip_button');
};

// The close button in the corner is live over the survey and takes the completion screen down
// without asking, so this is the way out of a lesson survey, which offers no skip button at all.
// Reported so the funnel shows a student leaving rather than a `started` that stops dead.
const handleFeedbackSurveyAbandoned = (answeredCount: number) => {
  if (previewMode) {
    return;
  }
  trackSurveySkipped(answeredCount, 'closed');
};
</script>

<template>
  <KUIUniCompletion
    v-model:skill-answer="skillAnswer"
    :coins-earned="completionRewards.coinsEarned"
    :total-coins="completionRewards.totalCoins"
    :animate-coins="completionRewards.animateCoins"
    :partial-unlock-message="partialUnlockMessage"
    :is-re-completion="completionResult?.wasReCompletion ?? null"
    :activities="collectionCompletion?.activities ?? []"
    :badge-url="collectionCompletion?.badgeUrl ?? null"
    :premium-badge-url="collectionCompletion?.premiumBadgeUrl ?? null"
    :badge-disclaimer="collectionCompletion?.badgeDisclaimer ?? null"
    :bonus-coins="completionRewards.bonusCoins"
    :progress-heading="collectionCompletion?.progressHeading ?? ''"
    :is-ghost-user="isConsumerGhost"
    :cta-loading="completionCtaLoading"
    :secondary-cta-loading="secondaryCtaLoading"
    :cta-label="primaryCtaLabel"
    :secondary-cta-label="secondaryCtaLabel"
    :prize="completionRewards.prize"
    :prize-outcome="completionRewards.prizeOutcome"
    :terms-to="campaignTermsUrl"
    :confirm-prize-loading="confirmPrizeLoading"
    :confirm-prize-error="confirmPrizeError"
    :asset-name="asset?.name ?? ''"
    :collection-name="collectionName ?? null"
    :partner-bg-color="collectionCompletion?.partnerBgColor || undefined"
    :animations-enabled="accessibilityStore.animationsEnabled"
    :feedback-survey="completionSurvey"
    :is-fully-completed="collectionCompletion?.isFullyCompleted ?? false"
    :feedback-survey-submitting="feedbackSurveySubmitting"
    :feedback-survey-shared-with-teacher="surveySharedWithTeacher"
    :feedback-survey-anonymous-responses="collectionCompletion?.feedbackSurveyAnonymousResponses ?? false"
    :feedback-survey-error-message="feedbackSurveyError"
    :considerations="completionResult?.considerations ?? []"
    :clarity="completionResult?.clarity ?? null"
    :spotlights="spotlights"
    @spotlight-link-click="trackSpotlightLinkClick"
    @considerations-viewed="emit('considerationsViewed')"
    @clarity-viewed="emit('clarityViewed')"
    @continue="handleCompletionContinue"
    @secondary-cta-click="handleSecondaryCta"
    @cta-shown="handleCtaShown"
    @choose="emit('done')"
    @confirm-entry="handleConfirmEntry"
    @skip-prize-draw="handleSkipPrizeDraw"
    @cancel-prize-draw="handleCancelPrizeDraw"
    @feedback-survey-submitted="handleFeedbackSurveySubmitted"
    @feedback-survey-skipped="handleFeedbackSurveySkipped"
    @feedback-survey-abandoned="handleFeedbackSurveyAbandoned"
  />

  <UnifiedAccountVerificationDialog
    v-model:open="showProfileVerification"
    @verified="handleProfileVerified"
  />
</template>
